import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'
import type { PaginationMeta } from '../types/models.js'

export interface CityRef {
  id: string
  name: string
  slug: string
  status: string
}

export interface CityListResult {
  data: CityRef[]
  meta: PaginationMeta
}

export async function getCities(): Promise<CityRef[]> {
  const res = await fetch(`${API_BASE}/cities?pageSize=100`)
  if (!res.ok) {
    throw new ApiError(res.status, { code: 'REQUEST_FAILED', message: 'Failed to load cities' })
  }
  const body = await res.json()
  return body.data as CityRef[]
}

export async function listCities(
  query: { page?: number; pageSize?: number; search?: string } = {},
): Promise<CityListResult> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  if (query.search) params.set('search', query.search)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const res = await fetch(`${API_BASE}/cities${suffix}`)
  if (!res.ok) {
    throw new ApiError(res.status, { code: 'REQUEST_FAILED', message: 'Failed to load cities' })
  }
  return res.json() as Promise<CityListResult>
}

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody = (body as { error?: { code: string; message: string } } | null)?.error
    ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

export interface CityAdmin extends CityRef {}

export async function createCity(input: { name: string; slug?: string }): Promise<CityAdmin> {
  const res = await fetch(`${API_BASE}/cities`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create city')
  const body = await res.json()
  return body.data
}

export async function updateCity(
  cityId: string,
  input: { name?: string; slug?: string; status?: string },
): Promise<CityAdmin> {
  const res = await fetch(`${API_BASE}/cities/${cityId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update city')
  const body = await res.json()
  return body.data
}

export async function deleteCity(cityId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/cities/${encodeURIComponent(cityId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to delete city')
}
